import { TERMS_VERSION, addressPartsOf, addressProblems, addressSettings, emptyAddress, formatAddress, siteSameAsSeat, type AddressParts, type AddressProblem } from "@szerviz/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LegalDocument } from "../screens/Legal";
import type { LegalDoc } from "../legal";
import { useApp } from "../context";
import type { ContractDetails } from "../data";
import { AddressFields, addressProblemNames } from "./AddressFields";
import { ErrorText } from "./ErrorText";

/**
 * Before the owner works in the program, the workshop accepts the general terms
 * (with the highlighted liability and amendment clauses) and the data processing
 * agreement. Shown again when TERMS_VERSION changes.
 */
export function TermsGate({ onAccept, onSignOut }: { onAccept: (details: ContractDetails) => Promise<void>; onSignOut: () => void }) {
  const { t, i18n } = useTranslation();
  const { workshop, store, reloadWorkshop } = useApp();
  const general = workshop.general as Record<string, string | null | undefined>;
  // who concludes the contract: the business (the legal name), recorded with the acceptance (owner, 2026-11-04)
  const [company, setCompany] = useState(general.legalName ?? "");
  const [seat, setSeat] = useState<AddressParts>(() => addressPartsOf(workshop.general, "seat"));
  // the workshop as the customers know it: its name (prefilled) and where the car is (the seat unless the tick is off)
  const [shopName, setShopName] = useState(workshop.name);
  const [sameAsSeat, setSameAsSeat] = useState(siteSameAsSeat(general));
  const [site, setSite] = useState<AddressParts>(() => (siteSameAsSeat(general) ? emptyAddress() : addressPartsOf(workshop.general, "workshop")));
  const [taxNumber, setTaxNumber] = useState(general.workshopTaxNumber ?? "");
  const [regNumber, setRegNumber] = useState(general.workshopRegNumber ?? "");
  const [terms, setTerms] = useState(false);
  const [dpa, setDpa] = useState(false);
  const [reading, setReading] = useState<LegalDoc | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // the seat is needed for the contract (and for invoices): all of its parts
  const seatProblems = addressProblems(seat, true);
  const siteProblems = sameAsSeat ? [] : addressProblems(site, true);
  const missing = [
    !company.trim() && t("terms.gate.company"),
    ...addressProblemNames(seatProblems, t("terms.gate.address"), t),
    !taxNumber.trim() && t("terms.gate.taxNumber"),
    !shopName.trim() && t("workshop.name"),
    ...addressProblemNames(siteProblems, t("setting.workshopAddress.label"), t),
    !terms && t("terms.gate.termsShort"),
    !dpa && t("terms.gate.dpaShort"),
  ].filter(Boolean) as string[];
  const marks = (problems: AddressProblem[]) => Object.fromEntries(problems.map((p) => [p === "postalCodeFormat" ? "postalCode" : p, true]));

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await store.saveGeneralSettings(workshop.id, {
        ...(workshop.general as Record<string, number | string | boolean | null>),
        legalName: company.trim(),
        ...addressSettings("seat", seat, workshop.language),
        workshopAddressSameAsSeat: sameAsSeat,
        ...addressSettings("workshop", sameAsSeat ? seat : site, workshop.language),
        workshopTaxNumber: taxNumber.trim(),
        workshopRegNumber: regNumber.trim() || null,
      });
      if (shopName.replace(/\s+/g, " ").trim() !== workshop.name) await store.renameWorkshop(workshop.id, shopName);
      await onAccept({ companyName: company.trim(), address: formatAddress(seat, { language: workshop.language }), taxNumber: taxNumber.trim(), regNumber: regNumber.trim() || null });
      await reloadWorkshop();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal terms-gate" role="dialog" aria-modal="true" aria-labelledby="terms-gate-title">
        <h2 id="terms-gate-title">{t("terms.gate.title")}</h2>
        <p>{t("terms.gate.intro", { version: TERMS_VERSION })}</p>
        <fieldset className="contract-party">
          <legend>{t("terms.gate.party")}</legend>
          <label className={`field${company.trim() ? "" : " invalid"}`}>
            <span>{t("terms.gate.company")}</span>
            <input id="contract-company" aria-label={t("terms.gate.company")} value={company} onChange={(e) => setCompany(e.target.value)} />
            <small>{t("terms.gate.companyHint")}</small>
          </label>
          <AddressFields idPrefix="contract-seat" legend={t("terms.gate.address")} value={seat} onChange={setSeat} invalid={marks(seatProblems)} required={{ postalCode: true, city: true, street: true }} />
          <label className={`field${taxNumber.trim() ? "" : " invalid"}`}>
            <span>{t("terms.gate.taxNumber")}</span>
            <input id="contract-tax" aria-label={t("terms.gate.taxNumber")} value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} />
          </label>
          <label className="field">
            <span>{t("terms.gate.regNumber")}</span>
            <input id="contract-reg" aria-label={t("terms.gate.regNumber")} value={regNumber} onChange={(e) => setRegNumber(e.target.value)} />
          </label>
          <p className="muted">{t("terms.gate.partyNote")}</p>
        </fieldset>
        <fieldset className="contract-party">
          <legend>{t("terms.gate.workshopPart")}</legend>
          <label className={`field${shopName.trim() ? "" : " invalid"}`}>
            <span>{t("workshop.name")}</span>
            <input id="contract-shop" aria-label={t("workshop.name")} value={shopName} onChange={(e) => setShopName(e.target.value)} />
            <small>{t("workshop.nameHint")}</small>
          </label>
          <label className="check-row">
            <input type="checkbox" checked={sameAsSeat} onChange={(e) => setSameAsSeat(e.target.checked)} />
            <span>{t("setting.workshopAddressSameAsSeat.label")}</span>
          </label>
          {!sameAsSeat && (
            <AddressFields idPrefix="contract-site" legend={t("setting.workshopAddress.label")} value={site} onChange={setSite} invalid={marks(siteProblems)} required={{ postalCode: true, city: true, street: true }} />
          )}
        </fieldset>
        <p className="warn-box">{t("terms.gate.highlight")}</p>
        <label className={`check-row terms-check${terms ? "" : " unchecked"}`}>
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
          <span>{t("terms.gate.acceptTerms")}</span>
        </label>
        <button type="button" className="link" onClick={() => setReading(reading === "terms" ? null : "terms")}>
          {t(reading === "terms" ? "terms.gate.hide" : "terms.gate.read", { doc: t("legal.doc.terms") })}
        </button>
        <label className={`check-row terms-check${dpa ? "" : " unchecked"}`}>
          <input type="checkbox" checked={dpa} onChange={(e) => setDpa(e.target.checked)} />
          <span>{t("terms.gate.acceptDpa")}</span>
        </label>
        <button type="button" className="link" onClick={() => setReading(reading === "dpa" ? null : "dpa")}>
          {t(reading === "dpa" ? "terms.gate.hide" : "terms.gate.read", { doc: t("legal.doc.dpa") })}
        </button>
        {reading && (
          <div className="terms-reader">
            <LegalDocument doc={reading} language={i18n.language} />
          </div>
        )}
        <ErrorText error={error} />
        <div className="actions">
          {missing.length > 0 && <span className="missing">{t("required.stillMissing", { fields: missing.join(", ") })}</span>}
          <button type="button" onClick={onSignOut}>
            {t("auth.signOut")}
          </button>
          <button type="button" className="primary" disabled={missing.length > 0 || busy} onClick={() => void accept()}>
            {t("terms.gate.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
